/**
 * Bottom-bar summary text for the mobile workspace drawer (Phase 9).
 *
 * Pure function — see research.md §R7. Three branches:
 *  - Snapshot present → numeric range with photo / anchor counts.
 *  - Missing inputs (or no compute yet) → section labels.
 *  - Empty estimate (no photos, no anchors, no snapshot) → "Project details".
 */

import type { EstimateStreamPayload } from "@starz/shared/api/types.gen";

export interface BottomBarState {
  photoCount: number;
  anchorCount: number;
  snapshot: EstimateStreamPayload | null;
}

export function formatBottomBarSummary(state: BottomBarState): string {
  const { photoCount, anchorCount, snapshot } = state;

  // Snapshot with a real range — lead with the dollars.
  if (snapshot?.kind === "snapshot" && snapshot.snapshot) {
    const range = formatRangeCompact(
      snapshot.snapshot.subtotal_low_cents,
      snapshot.snapshot.subtotal_high_cents,
    );
    const photoLabel = `${photoCount} ${photoCount === 1 ? "photo" : "photos"}`;
    const anchorLabel = `${anchorCount} ${anchorCount === 1 ? "anchor" : "anchors"}`;
    return `${photoLabel} · ${anchorLabel} · ${range}`;
  }

  // Truly empty — no photos, no anchors, no snapshot.
  if (photoCount === 0 && anchorCount === 0) {
    return "Project details";
  }

  // Some progress but no compute — surface the section labels.
  return "Photos · Measurements · Estimate";
}

/** Compact range like "$8.4 K – $11.2 K". Uses thin-space + en-dash. */
function formatRangeCompact(lowCents: number, highCents: number): string {
  return `${formatCompact(lowCents)} – ${formatCompact(highCents)}`;
}

function formatCompact(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1)} K`;
  }
  return `$${Math.round(dollars).toLocaleString("en-US")}`;
}
